import { isProduction } from './constants';
import { SVError } from './errors';

/**
 * scroll the window to given location from coming options
 * @param options Optional window DOM scrolling object
 */
export function scrollTo(options: ScrollToOptions = { behavior: 'smooth', left: 0, top: 100 }) {
  try {
    //scroll API - https://developer.mozilla.org/en-US/docs/Web/API/Window/scrollTo
    window.scrollTo(options);
  } catch (error) {
    // just a fallback for older browsers
    window.scrollTo(0, 0);
  }
}

type ElementOffset = {
  left: number;
  top: number;
};

/**
 * Returns an element's position relative to the whole document (page).
 *
 * If the element does not exist, returns O/O (top-left window corner).
 *
 * @example getOffset(document.getElementById('#element'));
 *
 * @param el
 * @see https://stackoverflow.com/a/28222246/2391795
 */
export const getElementOffset = (el: Element | null): ElementOffset => {
  const rect = el?.getBoundingClientRect();

  return {
    left: (rect?.left || 0) + window?.scrollX,
    top: (rect?.top || 0) + window?.scrollY
  };
};

export function scrollToError() {
  // Check if there are any errors, if so scroll to first error element
  const element = document.querySelector('*[aria-invalid=true]');
  if (element !== null) {
    const labelledByElement = document.querySelector(
      `*[id="${element.getAttribute('aria-labelledby')}"]`
    );
    const scrollElement = labelledByElement || element;
    const offsets = getElementOffset(scrollElement);
    window.scrollTo({ top: offsets.top, left: offsets.left, behavior: 'smooth' });
  }
}

/**
 * Retrieve the full Domain url
 */
export const getDomainUrl = () => {
  const location = window?.location;
  if (!location) {
    throw new SVError('Unable to retireve the window.location object');
  }
  const { protocol, port } = location;
  const slashes = protocol.concat('//');
  return slashes.concat(`${location.hostname}:${port}`);
};

/**
 * Determines whether a given pair of anchor values are equal, ignoring case and the leading # character if present
 *
 * @param lhsAnchor
 * @param rhsAnchor
 */
export const isAnchorEqual = (lhsAnchor: string, rhsAnchor: string) => {
  // Strip leading # if present, and convert to lowercase
  const normaliseAnchorString = (hash: string) => hash.replace(/^#/, '').toLowerCase();

  return normaliseAnchorString(lhsAnchor) === normaliseAnchorString(rhsAnchor);
};

/**
 * convert file size in bytes to other formats (ref: https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript)
 * @param bytes size of bytes in a file
 * @param decimals number of decimal points to format
 */
export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Wrapper to console a "log" message
 */
export const logMessage = !isProduction ? console.log : () => {};

/**
 * Wrapper to console a "warn" message
 */
export const warnMessage = !isProduction ? console.warn : () => {};
/**
 * Wrapper to console an "error" message
 */
export const errorMessage = console.error;
